<template>
  <div v-if="isVisibleTutorial" class="tutorial-pop">
    <div :class="['tutorial-con', tutorialContent.key]">
      <div :class="['tut-area', chatType.toLocaleLowerCase()]">
        <div class="inr">
          <component :is="tutorialContent.component" v-if="tutorialContent.position === 'prepend'" />
          <div :class="['speech-box', tutorialContent.position]">
            <div class="speech-head">
              <div class="num-pagenation">
                <span>{{ tutorialState.step }}</span>
                <span>/{{ tutorialState.maxStep }}</span>
              </div>
              <button class="close-btn" @click="closeTutorial">
                <IconClose />
              </button>
            </div>
            <div class="sb-txt-wrap">
              <strong class="slide-tit">{{ t(tutorialContent.title) }}</strong>
              <p class="slide-content">{{ t(tutorialContent.content) }}</p>
            </div>
            <div class="control-wrap">
              <button type="button" class="btn-primary slide-btn" @click="moveTutorial(true)">
                {{ t(tutorialContent.buttonText) }}
              </button>
            </div>
          </div>
          <component :is="tutorialContent.component" v-if="tutorialContent.position === 'append'" />
        </div>
      </div>
    </div>
  </div>
</template>

<script setup lang="ts">
import { isEmpty } from 'lodash';

import IconClose from '@/components/icons/IconClose.vue';
import { useTutorialStore } from '@/stores/tutorial';
import { TypeCall } from '@/types/common';
import { TutorialState, TutorialContent, TutorialType } from '@/types/components';
import { CHAT_TUTORIAL, SINGLE_CHAT_TUTORIAL, AUDIENCE_CHAT_TUTORIAL } from '@/utils/constants/tutorial';

const props = withDefaults(
  defineProps<{
    chatType: TypeCall;
  }>(),
  {
    chatType: 'GENERAL',
  }
);

const tutorialStore = useTutorialStore();

const { t } = useI18n();

const tutorialType = computed<TutorialType>(() => {
  return (
    {
      GENERAL: 'chat',
      MEETING: 'chat',
      CONFERENCE: 'chat',
      SINGLE: 'singleChat',
      AUDIENCE: 'audienceChat',
    }[props.chatType] || 'chat'
  );
});

const tutorialState = computed<TutorialState>(() => {
  return tutorialStore.getTutorial(tutorialType.value);
});

const tutorialContents = computed<TutorialContent[]>(() => {
  return (
    {
      GENERAL: CHAT_TUTORIAL,
      MEETING: CHAT_TUTORIAL,
      CONFERENCE: CHAT_TUTORIAL,
      SINGLE: SINGLE_CHAT_TUTORIAL,
      AUDIENCE: AUDIENCE_CHAT_TUTORIAL,
    }[props.chatType] || []
  );
});

const tutorialContent = computed<TutorialContent>(() => {
  return tutorialContents.value[tutorialState.value.step - 1] ?? ({} as TutorialContent);
});

const isVisibleTutorial = computed<boolean>(() => {
  return !tutorialState.value.isCompleted && !isEmpty(tutorialContent.value);
});

const moveTutorial = (isNext: boolean) => {
  tutorialStore.setTutorialStep(tutorialType.value, isNext);
};

const closeTutorial = () => {
  tutorialStore.setTutorialComplete(tutorialType.value);
};
</script>

<style scoped lang="scss">
.tutorial-pop {
  position: fixed;
  width: 100%;
  height: 100%;
  left: 0;
  top: 0;
  background: $color-transparent-dark80;
  z-index: 999;

  .tutorial-con {
    width: calc(100% - 15rem);
    position: absolute;
    right: 0;
    bottom: 0;
    display: flex;
    justify-content: center;
    align-items: center;

    .tut-area {
      position: relative;
      display: flex;
      flex-direction: column;
      align-items: center;
      justify-content: center;
      max-width: 75rem;
      width: 100%;
      padding-bottom: calc(0.5rem + env(safe-area-inset-bottom));
      .inr {
        position: relative;
        width: fit-content;
        display: flex;
        flex-direction: column;
        align-items: center;
        justify-content: center;
      }
    }

    &.mic {
      .tut-area {
        &.conference {
          .inr {
            right: 110px;
          }
        }
        &.meeting {
          max-width: 500px;
          align-items: flex-start;
          .inr {
            right: 14px;
          }
        }
        &.audience {
          right: 78px;
        }
        &.general {
          .inr {
            right: 110px;
          }
        }
        &.single {
          .inr {
            right: 8px;
          }
        }
      }
    }

    &.language {
      .tut-area {
        &.meeting {
          .inr {
            right: -70px;
          }
        }
        &.audience {
          .inr {
            right: 5px;
          }
        }
      }
      .inr {
        right: -30px;
      }
    }
    &.qr {
      bottom: initial;
      top: 5px;

      .tut-area {
        max-width: 90rem;
        align-items: flex-end;
        padding: 0 1.25rem;
      }

      .inr {
        right: 136px;
      }
    }
    &.single-language-home {
      bottom: 44%;
      .tut-area {
        align-items: flex-start;
        padding: 0 1rem;
        max-width: 90rem;
        .inr {
          left: 0;
        }
      }
    }
    &.single-language-away {
      bottom: 52%;

      .tut-area {
        align-items: flex-end;
        padding: 0 1rem;
        max-width: 90rem;
        .inr {
          left: 0;
        }
      }
    }
  }

  .speech-box {
    background: $color-white;
    padding: 1.25rem;
    border-radius: $size-m;
    position: relative;
    margin-bottom: 1.5rem;
    width: 100%;

    &::after {
      content: '';
      position: absolute;
      width: 0;
      height: 0;
      left: 50%;
      bottom: -9px;
      transform: translateX(-50%);
      border-style: solid;
      border-width: 13px 12px 0 12px;
      border-color: #ffffff transparent transparent transparent;
    }

    &.append {
      margin-top: 0;
      margin-bottom: 1.5rem;
      &::after {
        bottom: -9px;
        top: initial;
        border-width: 13px 12px 0 12px;
        border-color: #ffffff transparent transparent transparent;
      }
    }
    &.prepend {
      margin-top: 1.5rem;
      margin-bottom: 0;
      &::after {
        bottom: initial;
        top: -9px;
        border-width: 0 12px 13px 12px;
        border-color: transparent transparent #ffffff transparent;
      }
    }

    .speech-head {
      display: flex;
      justify-content: space-between;
      align-items: center;
      padding-bottom: $size-m;
      .num-pagenation {
        span {
          font-weight: 600;
          white-space: nowrap;
          &:last-child {
            color: $color-gray-90;
          }
        }
      }
      .close-btn {
        width: 28px;
        height: 28px;
        border-radius: 50%;
        background: $gray10;
        cursor: pointer;
        svg {
          color: $color-gray-400;
        }
      }
    }
    .sb-txt-wrap {
      text-align: center;
      .slide-tit {
        white-space: pre-wrap;
        line-height: 130%;
        display: block;
        font-weight: 600;
        font-size: $font-size-h6;
        margin-bottom: $size-s;
      }
      p {
        white-space: pre-wrap;
        line-height: 130%;
        color: $color-gray-900;
      }
    }

    .control-wrap {
      padding-top: 1rem;
      display: flex;
      align-items: center;
      justify-content: center;
      gap: $size-s;

      .slide-btn {
        width: 100%;
        padding: 0 1.5rem;
        height: 2.5rem;
        border-radius: $size-s;
        font-size: $font-size-psmall;
      }
    }
  }
}

@media (max-width: 1440px) {
  .tutorial-pop {
    .tutorial-con {
      &.mic {
        .tut-area {
          &.meeting {
            max-width: 420px;
          }
        }
      }
      &.language {
        .tut-area {
          &.meeting {
            max-width: 400px;
            .inr {
              right: -24px;
            }
          }
        }
      }
    }
  }
}

@include respond-to(tablet) {
  .tutorial-pop {
    .tutorial-con {
      width: calc(100% - 72px);

      &.qr {
        .inr {
          right: 110px;
        }
      }
    }
  }
}

@media (max-width: 840px) {
  .tutorial-pop {
    .tutorial-con {
      &.mic {
        .tut-area {
          &.conference {
            max-width: 316px;
            align-items: flex-start;
            .inr {
              right: 0;
            }
          }
          &.meeting {
            max-width: 290px;
          }
          &.audience {
            max-width: 300px;
            right: 0;
            .inr {
              right: 12px;
            }
          }
          &.general {
            max-width: 320px;
            align-items: flex-start;
            .inr {
              right: -4px;
              padding: 0;
            }
          }
        }
      }
      &.language {
        .tut-area {
          &.conference {
            max-width: 295px;
            align-items: flex-end;
          }
          &.meeting {
            max-width: 300px;
            align-items: flex-end;
            .inr {
              right: 0;
              align-items: flex-end;
              .speech-box {
                &:after {
                  left: auto;
                  right: 20px;
                  transform: translateX(0);
                }
              }
            }
          }
          &.audience {
            .inr {
              align-items: flex-end;
              .speech-box {
                &::after {
                  left: inherit;
                  right: 40px;
                  transform: translateX(0);
                }
              }
            }
          }
        }
        .inr {
          right: -90px;
        }
      }
      &.qr {
        .inr {
          right: 155px;
        }
      }
    }
  }
}

@include respond-to(mobile-landscape) {
  .tutorial-pop {
    .tutorial-con {
      width: 100%;
      &.mic {
        .tut-area {
          &.conference {
            max-width: 280px;
            .inr {
              width: 100%;
              right: 0;
              left: -48px;
              .speech-box {
                left: 48px;
                &::after {
                  left: 60px;
                }
              }
            }
          }
          &.meeting {
            max-width: 320px;
          }
        }
        .inr {
          padding: 0 1.25rem;
          &.meeting {
            right: 20px;
            align-items: flex-start;
            .speech-box {
              &:after {
                left: 40px;
              }
            }
          }
        }
      }
      &.language {
        .tut-area {
          &.general {
            .inr {
              right: -10px;
            }
          }
        }
        .inr {
          align-items: flex-end;
          right: 0;
        }
        .speech-box {
          &::after {
            left: inherit;
            right: 40px;
            transform: translateX(0);
          }
        }
      }
      &.qr {
        .inr {
          right: 32px;
        }
        .tut-area {
          width: calc(100% - 98px);
        }
      }
    }
  }
}
</style>
